import React from "react";

import type {
  Comments as CommentsReport,
  Teams as TeamsReport,
} from "apps/company/api/rest/company-dashboard-engagement";
import type { EngagementTiles } from "apps/company/api/rest/company-dashboard-tiles";
import { useTranslation } from "lib/i18n/use-translation";

import { StatCard } from "../engagement/EngagementTab";
import { engagementView } from "../engagement/engagement-view";
import engagementStyles from "../engagement/engagement.module.scss";
import Comments from "./Comments";
import styles from "./engagement-redesign.module.scss";
import Funnel from "./Funnel";
import Popular from "./Popular";
import Teams from "./Teams";
import Topics from "./Topics";

const K = "admin:companyDashboard.engagement";

// Separate from `EngagementFigures` so the flag-off page stays as it is.
const RedesignFigures = ({
  tiles,
  comments,
  teams,
  filtered,
  day,
  quarter,
}: {
  tiles: EngagementTiles;
  comments: CommentsReport | undefined;
  teams: TeamsReport | undefined;
  // Whether the figures are for one group rather than everyone.
  filtered: boolean;
  quarter: string;
  day: string;
}) => {
  const { t, i18n } = useTranslation(["admin"]);
  const view = engagementView(tiles, i18n.language);

  return (
    <>
      <h3 className={engagementStyles["section-label"]}>
        {t(`${K}.sections.activation`)}
      </h3>
      <div className={engagementStyles["stats"]}>
        <StatCard
          label={t(`${K}.stats.employeesInScope`)}
          value={view.employeesInScope.value}
          info={t(`${K}.stats.employeesInScopeInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.activated`)}
          value={view.activated.value}
          detail={
            view.activated.share &&
            t(`${K}.stats.ofWorkforce`, { share: view.activated.share })
          }
          info={t(`${K}.stats.activatedInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.everEngaged`)}
          value={view.everEngaged.value}
          detail={
            view.engagedOfWorkforce &&
            t(`${K}.stats.ofWorkforce`, { share: view.engagedOfWorkforce })
          }
          info={t(`${K}.stats.everEngagedOfWorkforceInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.minutes`)}
          value={view.minutes.value}
          detail={
            view.minutes.average &&
            t(`${K}.stats.minutesDetail`, { minutes: view.minutes.average })
          }
          info={t(`${K}.stats.minutesInfo`)}
        />
      </div>
      {teams !== undefined && (
        <div className={styles["teams-section"]}>
          <Teams teams={teams} day={day} filtered={filtered} />
        </div>
      )}
      <h3 className={engagementStyles["section-label"]}>
        {t(`${K}.sections.engagement`)}
      </h3>
      <div className={engagementStyles["cards"]}>
        <Funnel funnel={view.funnel} day={day} />
        <Topics topics={view.topics} day={day} />
        <Popular popular={view.popular} quarter={quarter} day={day} />
      </div>
      {comments !== undefined && <Comments comments={comments} day={day} />}
    </>
  );
};

export default RedesignFigures;
